import { ClipboardEvent, useContext, useEffect, useState } from 'react'

import {
  Box,
  Button,
  DragNDrop,
  Icon,
  Input,
  Text,
} from '@fairfashion/design-system'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { ConfirmModalContext } from '../../components/ConfirmModal'
import { sdk } from '../../services/sdk'
import { QRCodeContext, QRCodeProvider } from '../QRCode/qrcode.context'

import * as Styles from './qrcode-edit-step.styles'
import { EditStepForm } from './qrcode-edit-step.types'

export const QRCodeEditStepComp = () => {
  const [error, setError] = useState('')
  const [filesToDelete, setFilesToDelete] = useState<string[]>([])

  const { companyProductionStep, actions } = useContext(QRCodeContext)
  const { confirmModal } = useContext(ConfirmModalContext)

  const { register, reset, watch, getValues, setValue, handleSubmit } =
    useForm<EditStepForm>()

  const params = useParams()
  const navigate = useNavigate()

  useEffect(() => {
    actions.getCompanyProductionStep(params.stepId ?? '')
  }, [])

  useEffect(() => {
    if (companyProductionStep) {
      reset({
        name: companyProductionStep.name,
        description: companyProductionStep.description,
        videos: companyProductionStep.assets?.videos ?? [],
        images: companyProductionStep.assets?.images ?? [],
        videoInput: '',
      })
    }
  }, [companyProductionStep])

  const videos = watch('videos')

  const handleAddVideo = () => {
    const videoInput = getValues('videoInput')

    if (videoInput) {
      const prefix = 'https://www.youtube.com/watch?v='
      const videoAlreadyExists = videos?.find((v) => v === prefix + videoInput)

      if (!videoAlreadyExists) {
        setValue('videos', [...videos, prefix + videoInput])
      }

      setValue('videoInput', '')
    }
  }

  const handlePasteVideoUrl = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pattern1 = /(?:\?|&)v=([a-zA-Z0-9_-]+)/
    const pattern2 = /youtu\.be\/([a-zA-Z0-9_-]+)/
    const text = e.clipboardData.getData('text/plain')
    const match1 = text.match(pattern1)
    const match2 = text.match(pattern2)

    if (match1) {
      setValue('videoInput', match1[1])
      return
    }
    if (match2) {
      setValue('videoInput', match2[1])
      return
    }

    setValue('videoInput', text)
  }

  const handleDeleteVideo = async (index: number) => {
    if (
      await confirmModal(
        'Excluir vídeo',
        'Tem certeza que deseja excluir este vídeo?',
      )
    ) {
      const newVideos = videos?.filter((video, i) => i !== index)
      setValue('videos', newVideos)
    }
  }

  const onSubmit = async (data: EditStepForm) => {
    try {
      await sdk.companyProductionSteps.udateCompanyProductionStep(
        params.stepId ?? '',
        {
          name: data.name,
          description: data.description,
          assets: {
            videos: data.videos,
          },
        },
      )

      if (filesToDelete.length) {
        for (const file of filesToDelete) {
          await sdk.companyProductionSteps.deleteCompanyProductionStepImage(
            params.stepId ?? '',
            file,
          )
        }
      }

      if (data.imagesFiles?.length) {
        const formData = new FormData()
        for (let i = 0; i < data.imagesFiles.length; i++) {
          formData.append('images', data.imagesFiles[i])
        }

        await sdk.companyProductionSteps.uploadCompanyProductionStepAssets(
          params.stepId ?? '',
          formData,
        )
      }

      navigate(`/qrcode/${params.code}/journey`)
    } catch (err) {
      console.log(err)
      setError((err as any)?.error?.message ?? '')
    }
  }

  const theme = useTheme()

  const handleChangeFiles = (files?: File[]) => {
    setValue('imagesFiles', files || [])
  }

  const handleDeleteDefaultFile = (index: number) => {
    const fileToDelete = getValues('images')[index]
    const newImages = getValues('images').filter((_, i) => i !== index)
    setValue('images', newImages)

    setFilesToDelete([...filesToDelete, fileToDelete])
  }

  const images = watch('images')

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box direction="column" width="100%" gap={2} padding={3}>
        <Text size="medium">Editar etapa de produção</Text>

        <DragNDrop
          multiple
          fileType=".png, .jpg, .jpeg, .webp, image/png, image/jpeg, image/webp"
          onChange={handleChangeFiles}
          onDeleteDefaultFile={handleDeleteDefaultFile}
          preview="image"
          defaultFiles={images}
          fileTypeError="Tipo de arquivo inválido"
        />

        <Box gap={2} flexWrap="wrap" width="100%">
          <Box flex={1} minWidth="180px">
            <Input {...register('name')} label="Nome" />
          </Box>
          <Box flex={2} minWidth="180px">
            <Input {...register('description')} label="Descrição" />
          </Box>
        </Box>

        <Box
          padding={2}
          borderRadius="20px"
          border={{
            color: 'grey_5',
            width: 1,
          }}
          boxShadow={theme.box_shadow.card}
          direction="column"
          gap={2}
          width="100%"
        >
          <Text>Vídeos</Text>
          <Input
            {...register('videoInput')}
            label="URL do vídeo no YouTube"
            placeholder="ID do vídeo"
            adornment={
              <Text color="grey_3">https://www.youtube.com/watch?v=</Text>
            }
            adornmentPosition="left"
            onPaste={handlePasteVideoUrl}
          />
          <Button variant="outlined" onClick={handleAddVideo}>
            <Icon name="add" color="grey_1" />
            Adicionar URL
          </Button>
          <Box gap={2} flexWrap="wrap">
            {videos?.map((video, index) => (
              <Styles.VideoTag key={`video-${index}`} gap={1}>
                <Styles.VideoText size="small">
                  <a href={video} target="_blank">
                    {video}
                  </a>
                </Styles.VideoText>

                <Button
                  variant="text"
                  padding="0"
                  onClick={() => handleDeleteVideo(index)}
                >
                  <Icon name="close" color="grey_1" />
                </Button>
              </Styles.VideoTag>
            ))}
          </Box>
        </Box>

        <Box width="100%" gap={2} alignItems="flex-end" direction="column">
          {error && (
            <Box>
              <Text color="error">{error}</Text>
            </Box>
          )}
          <Box width="100%" gap={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={() => navigate(`/qrcode/${params.code}/journey`)}
            >
              Cancelar
            </Button>
            <Button type="submit">Salvar</Button>
          </Box>
        </Box>
      </Box>
    </form>
  )
}

export const QRCodeEditStep = () => {
  return (
    <QRCodeProvider>
      <QRCodeEditStepComp />
    </QRCodeProvider>
  )
}
